import { Children, cloneElement, isValidElement, useMemo, useState, type ReactElement, type ReactNode } from "react"
import type { SortingState } from "@tanstack/react-table"

import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "../componentes/Empty"
import * as Icones from "../componentes/Icones"
import { Button } from "../componentes/Button"
import { TablePrincipal2 } from "../componentes/TablePrincipal2"
import { cn } from "../lib/cn"

const POR_PAGINA = 10

type Ordenavel = string | number | null | undefined

export function TabelaDaFicha<T>({
  dados,
  children,
  vazio,
  ordenar = {},
  abasNaBarra,
  filtrosNaBarra,
  acaoNoTopo,
  detalheDaLinha,
  linhaAberta,
  aoClicarNaLinha,
  linhaClicavel,
  linhaSelecionada,
}: {
  dados: T[]
  children: ReactNode
  vazio: ReactNode
  ordenar?: Record<string, (linha: T) => Ordenavel>
  abasNaBarra?: ReactNode
  filtrosNaBarra?: ReactNode
  acaoNoTopo?: ReactNode
  detalheDaLinha?: (linha: T) => ReactNode
  linhaAberta?: (linha: T) => boolean
  aoClicarNaLinha?: (linha: T) => void
  linhaClicavel?: (linha: T) => boolean
  linhaSelecionada?: (linha: T) => boolean
}) {
  const [pagina, definirPagina] = useState(1)
  const [porPagina, definirPorPagina] = useState(POR_PAGINA)
  const [ordem, definirOrdem] = useState<SortingState>([])

  const ordenados = useMemo(() => {
    const pedida = ordem[0]
    const chave = pedida === undefined ? undefined : ordenar[pedida.id]
    if (pedida === undefined || chave === undefined) return dados
    const sinal = pedida.desc ? -1 : 1
    return [...dados].sort((a, b) => {
      const x = chave(a)
      const y = chave(b)
      if (x === y) return 0
      if (x === null || x === undefined) return 1
      if (y === null || y === undefined) return -1
      return (x < y ? -1 : 1) * sinal
    })
  }, [dados, ordem, ordenar])

  const total = ordenados.length
  const paginas = Math.max(1, Math.ceil(total / porPagina))
  const atual = Math.min(pagina, paginas)
  const visiveis = ordenados.slice((atual - 1) * porPagina, atual * porPagina)

  const colunas = Children.map(children, (filho) => {
    if (!isValidElement(filho) || (filho.type as { displayName?: string }).displayName !== "Column") return filho
    const coluna = filho as ReactElement<{ name: string; enableSorting?: boolean }>
    return cloneElement(coluna, { enableSorting: coluna.props.name in ordenar })
  })

  return (
    <TablePrincipal2<T>
      data={visiveis}
      listrada
      paginacao={total > POR_PAGINA}
      pageCount={paginas}
      currentPage={atual}
      pageSize={porPagina}
      totalRegistros={total}
      onPageChange={definirPagina}
      onPageSizeChange={(tamanho) => {
        definirPorPagina(tamanho)
        definirPagina(1)
      }}
      ordenacaoNoServidor
      ordenacao={ordem}
      onSortingChange={(nova) => {
        definirOrdem(nova)
        definirPagina(1)
      }}
      mensagemVazia={vazio}
      abasNaBarra={abasNaBarra}
      filtrosNaBarra={filtrosNaBarra}
      acaoNoTopo={acaoNoTopo}
      detalheDaLinha={detalheDaLinha}
      linhaAberta={linhaAberta}
      aoClicarNaLinha={aoClicarNaLinha}
      linhaClicavel={linhaClicavel}
      linhaSelecionada={linhaSelecionada}
    >
      {colunas}
    </TablePrincipal2>
  )
}

export function useLinhasAbertas<K>() {
  const [abertas, definirAbertas] = useState<Set<K>>(() => new Set())
  return {
    aberta: (chave: K) => abertas.has(chave),
    alternar: (chave: K) =>
      definirAbertas((atuais) => {
        const novas = new Set(atuais)
        if (novas.has(chave)) novas.delete(chave)
        else novas.add(chave)
        return novas
      }),
  }
}

export function DetalheDaFicha({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <div data-slot="detalhe-da-ficha" className="flex flex-col gap-1 text-sm">
      <span className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">{rotulo}</span>
      <span className="whitespace-pre-line text-foreground first-letter:uppercase">{children}</span>
    </div>
  )
}

export function VazioDaFicha({ titulo, descricao, acao }: { titulo: string; descricao?: string; acao?: ReactNode }) {
  return (
    <Empty className="py-6">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <Icones.Leitura />
        </EmptyMedia>
        <EmptyTitle>{titulo}</EmptyTitle>
        {descricao !== undefined && <EmptyDescription>{descricao}</EmptyDescription>}
      </EmptyHeader>
      {acao !== undefined && <EmptyContent>{acao}</EmptyContent>}
    </Empty>
  )
}

export const SUCESSO =
  "border-transparent bg-status-sucesso text-sobre-status-sucesso hover:bg-status-sucesso-pressionado hover:text-sobre-status-sucesso"

export const COLUNA_NUMERICA = "text-center [&>button]:w-full [&>button]:justify-center"

export const COLUNA_DE_ACOES = "md:w-px md:whitespace-nowrap md:text-center"

export type AcaoDaLinha = {
  rotulo: string
  icone: Icones.Icone
  aoClicar: () => void
  perigo?: boolean
  sucesso?: boolean
}

export function BotoesDaLinha({ acoes }: { acoes: (AcaoDaLinha | false | null | undefined)[] }) {
  const visiveis = acoes.filter((acao): acao is AcaoDaLinha => Boolean(acao))
  if (visiveis.length === 0) return <span className="text-muted-foreground">—</span>
  return (
    <div data-slot="botoes-da-linha" className="flex flex-wrap justify-end gap-1.5 md:flex-nowrap">
      {visiveis.map(({ rotulo, icone: Icone, aoClicar, perigo, sucesso }) => (
        <Button
          key={rotulo}
          type="button"
          variant="outline"
          size="sm"
          className={cn("h-7 text-xs", perigo && "border-destructive text-destructive hover:text-destructive", sucesso && SUCESSO)}
          onClick={aoClicar}
        >
          <Icone />
          {rotulo}
        </Button>
      ))}
    </div>
  )
}
